import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * React error boundary used both at the SPA root and per-route.
 * Catches render-time errors that escape feature-level boundaries
 * and presents a "support escalation" fallback rather than a blank
 * iframe.
 *
 * The customer surface runs inside Workfront's iframe — a blank page
 * is indistinguishable from "the iframe failed to load" and gives
 * the customer no path forward. This boundary at minimum tells them
 * the SPA crashed and surfaces the deployed commit so support can
 * match the bug to a release.
 *
 * Per-route usage: wrap the `<Outlet>` and key the boundary on
 * `location.pathname` — navigating to a different route unmounts
 * the boundary, which clears its error state. A user that bumps
 * into a broken page can pivot to a working one via the sidebar /
 * Cmd+K and the SPA recovers, without a full reload.
 */
interface State {
  error: Error | null;
}

interface Props {
  children: ReactNode;
  /** Optional scope name surfaced in the fallback ("dashboard", etc.). */
  scope?: string;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // CloudWatch Logs capture from the SPA goes through the future
    // `/v1/client-error` endpoint. For now, surface to the browser
    // console so the dev tools picks it up.
    // eslint-disable-next-line no-console
    console.error(
      `SPA error boundary${this.props.scope ? ` (${this.props.scope})` : ''} caught:`,
      error,
      info,
    );
  }

  reset = (): void => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    if (this.state.error) {
      return (
        <div className="relative z-10 flex min-h-[calc(100vh-7rem)] items-center justify-center p-6">
          <div className="max-w-xl rounded-2xl border border-rose-400/40 bg-rose-500/10 p-8 text-center">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-rose-500/20">
              <AlertTriangle className="h-6 w-6 text-rose-700 dark:text-rose-300" />
            </div>
            <h1 className="mb-2 text-2xl font-light text-foreground">Something went wrong</h1>
            <p className="mb-4 text-sm text-foreground/70">
              {this.props.scope
                ? `The ${this.props.scope} page hit a render error.`
                : "The customer app couldn't finish rendering."}{' '}
              Try again, or navigate elsewhere with the sidebar / Cmd+K.
            </p>
            <pre className="mb-4 overflow-x-auto whitespace-pre-wrap rounded-md border border-foreground/10 bg-black/30 p-3 text-left text-xs text-foreground/75">
              {this.state.error.message}
            </pre>
            <button
              type="button"
              onClick={this.reset}
              className="inline-flex items-center gap-2 rounded-md border border-foreground/15 bg-foreground/5 px-4 py-2 text-sm text-foreground/80 transition-colors hover:border-[color:var(--brand-yellow)] hover:text-foreground"
            >
              <RefreshCw className="h-4 w-4" /> Try again
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
